import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Check if we can detect the API version
const pdfJsApiVersion = pdfjs.version || '4.8.69'; // Default if not explicitly set
const workerVersion = '3.4.120';
const versionMismatch = pdfJsApiVersion !== workerVersion;

// Log version information for debugging
console.log(`PDF.js API version: ${pdfJsApiVersion}, Worker version: ${workerVersion}`);

// Try to load the worker from cdnjs which has better CORS support
// Use a version we know exists and set up fallback options
const workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${workerVersion}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

// Set up a fallback mechanism
const workerBlob = `
  // Minimal pdf.js worker
  self.onmessage = function (e) {
    console.log('Fake PDF.js worker received message', e.data);
    if (e.data && e.data.action === 'test') {
      self.postMessage({ action: 'test', result: true });
    }
  };
`;

// Create a fallback worker blob if loading fails
try {
  const blob = new Blob([workerBlob], { type: 'application/javascript' });
  const fallbackWorkerUrl = URL.createObjectURL(blob);
  
  // Test if the main worker fails to load
  fetch(workerSrc).catch(() => {
    console.log('Using fallback PDF.js worker');
    pdfjs.GlobalWorkerOptions.workerSrc = fallbackWorkerUrl;
  });
} catch (e) {
  console.error('Failed to create fallback worker', e);
}

/**
 * PDF viewer component for rendering PDF files with page navigation
 * @param {string} pdfUrl - URL to the PDF file to display
 * @param {boolean} fullWidth - Whether to use full width display mode
 */
const PDFViewer = ({ pdfUrl, fullWidth = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [iframeFallback, setIframeFallback] = useState(versionMismatch); // Start with iframe if version mismatch

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle successful document load
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  // Handle document load error
  function onDocumentLoadError(error) {
    console.error('Error loading PDF:', error);
    setError(error);
    // If we get an error, show iframe fallback
    setIframeFallback(true);
  }

  // Change page with bounds checking
  const changePage = (offset) => {
    setPageNumber(prevPageNumber => {
      const newPage = prevPageNumber + offset;
      return newPage >= 1 && newPage <= numPages ? newPage : prevPageNumber;
    });
  }

  // Go to a specific page
  const goToPage = (page) => {
    if (page >= 1 && page <= numPages) {
      setPageNumber(page);
    }
  }

  // Calculate dimensions for the PDF display
  const calculateDimensions = () => {
    // Consider available space, minus padding and margins
    const availableWidth = fullWidth ? windowWidth - 32 : Math.min(windowWidth - 32, 900); // 16px padding on each side, 900px max for standard
    const availableHeight = windowHeight - 200; // Account for header and controls
    
    return {
      width: availableWidth,
      height: availableHeight
    };
  }

  const dimensions = calculateDimensions();

  // If we've had an error or version mismatch and need to use iframe fallback
  if (iframeFallback && pdfUrl) {
    return (
      <div className={`pdf-viewer w-full ${fullWidth ? '' : 'max-w-5xl'} mx-auto`}>
        <div className="w-full flex flex-col items-center mb-2">
          <p className="text-sm text-gray-600 mb-1">Using simplified PDF viewer for better compatibility.</p>
          <a 
            href={pdfUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Open PDF in new tab
          </a>
        </div>
        <iframe 
          src={pdfUrl} 
          className={`w-full h-[80vh] border-0 rounded-lg`}
          title="PDF Document"
        />
      </div>
    );
  }

  return (
    <div className={`pdf-viewer w-full ${fullWidth ? '' : 'max-w-5xl'} mx-auto bg-gray-800 rounded-lg overflow-hidden flex flex-col`}>
      {error ? (
        <div className="text-white text-center p-8">
          <p className="text-red-400 font-bold">Error loading PDF</p>
          <p className="mt-2">Please try again later or contact support.</p>
          <div className="mt-4 p-2 bg-gray-700 rounded text-xs text-left overflow-auto max-h-32">
            <code>{error.toString()}</code>
          </div>
          <a 
            href={pdfUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Open PDF directly
          </a>
        </div>
      ) : (
        <>
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="loading text-white text-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
                <p>Loading document...</p>
              </div>
            }
            options={{
              cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/',
              cMapPacked: true,
              standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/',
            }}
            className="flex-grow flex items-center justify-center bg-gray-700 p-2"
          >
            <Page 
              pageNumber={pageNumber} 
              width={dimensions.width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="mx-auto bg-white rounded-lg"
            />
          </Document>
          
          {numPages && (
            <div className="pdf-controls p-3 flex items-center justify-between bg-gray-900">
              <button 
                disabled={pageNumber <= 1} 
                onClick={() => changePage(-1)}
                className="bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-600 disabled:opacity-50"
              >
                Previous
              </button>
              
              <div className="flex items-center">
                <span className="text-white">
                  Page {pageNumber} of {numPages}
                </span>
                
                <div className="mx-4 hidden md:flex items-center">
                  <input
                    type="number"
                    min="1"
                    max={numPages}
                    value={pageNumber}
                    onChange={(e) => goToPage(parseInt(e.target.value))}
                    className="w-16 px-2 py-1 rounded bg-gray-700 text-white text-center"
                  />
                  <button
                    onClick={() => goToPage(parseInt(pageNumber))}
                    className="ml-2 bg-gray-700 text-white px-2 py-1 rounded"
                  >
                    Go
                  </button>
                </div>
              </div>
              
              <button 
                disabled={pageNumber >= numPages} 
                onClick={() => changePage(1)}
                className="bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PDFViewer; 